import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

export type DialogAnuncioMode = 'CREATE_USER' | 'LINK_USER' | 'COMMENT' | 'NO_WORKSHIFT' | 'TERMINATE_PROJECT' | 'TERMINATE_EMPLOYER_CONTRACTS' | 'EMAIL_ALREADY_REGISTERED' | 'UNVERIFIED_EMAIL' | 'VERIFIED_EMAIL';

export interface DialogAnuncioData {
  mode: DialogAnuncioMode;
  title?: string;
  lines?: string[];
  cancelText?: string;
  confirmText?: string;
  item?: any;
}

interface DialogVm {
  title: string;
  lines: string[];
  cancelText: string;
  confirmText: string;
  singleButton?: boolean;
  icon?: string;
  headerBar?: boolean;
  sectionLabel?: string;
  introText?: string;
  numbered?: boolean;
  warningText?: string;
  customIcon?: 'unverified-email' | 'verified-email';
  primaryBlue?: boolean;
  centerText?: boolean;
  secondaryText?: string;
  showCloseIcon?: boolean;
}

@Component({
  selector: 'app-dialog-anuncio-informativos-generales',
  templateUrl: './dialog-anuncio-informativos-generales.component.html',
  styleUrls: ['./dialog-anuncio-informativos-generales.component.scss']
})
export class DialogAnuncioInformativosGeneralesComponent implements OnInit {

  vm!: DialogVm;
  newComment = '';
  commentTouched = false;

  private readonly defaults: Record<DialogAnuncioMode, DialogVm> = {
    CREATE_USER: {
      title: 'Este usuario no está creado, ¿desea crearlo?',
      lines: [
        'Antes de crear el usuario se recomienda validar el correo.',
        'El usuario recibirá un correo, debe ingresar al link enviado y terminar el registro y la aceptación de su usuario.'
      ],
      cancelText: 'CANCELAR',
      confirmText: 'CREAR Y VINCULAR'
    },
    LINK_USER: {
      title: 'Este usuario está creado pero no está verificado. ¿Desea vincularlo?',
      lines: [
        'El usuario recibirá un correo, deberá ingresar al link enviado y verificar el usuario.'
      ],
      cancelText: 'CANCELAR',
      confirmText: 'VINCULAR'
    },
    COMMENT: {
      title: 'COMENTARIO',
      lines: [],
      cancelText: 'CANCELAR',
      confirmText: 'GUARDAR'
    },
    NO_WORKSHIFT: {
      title: 'SIN LUGAR DE TRABAJO ASIGNADO',
      lines: [
        'El empleado tiene un lugar de trabajo asignado, pero no cuenta con un horario de' +
        ' trabajo configurado para el período seleccionado.',
        'Verifica la asignación del horario para poder calcular correctamente la prenómina.'
      ],
      cancelText: 'CANCELAR',
      confirmText: 'ENTENDIDO',
      singleButton: true,
      icon: 'warning'
    },
    EMAIL_ALREADY_REGISTERED: {
      title: 'Correo registrado y verificado',
      lines: [
        'El correo ingresado ya se encuentra registrado y verificado.'
      ],
      cancelText: 'CANCELAR',
      confirmText: 'Cerrar',
      singleButton: true,
      icon: 'error_outline'
    },
    TERMINATE_PROJECT: {
      title: 'LIQUIDAR PROYECTO',
      lines: [
        'Todas las etapas del proyecto serán inactivadas.',
        'Todos los contratos que solo estén vinculados a este proyecto serán inactivados.',
        'Todas las personas y vehículos vinculados a contratos que solo estén vinculados a este proyecto serán inactivados.',
        'Todos los usuarios vinculados a este proyecto serán inactivados.',
        'Todos los usuarios vinculados a contratos que solo estén vinculados a este proyecto serán inactivados.'
      ],
      cancelText: 'Cerrar',
      confirmText: 'Iniciar',
      headerBar: true,
      sectionLabel: 'Instrucciones',
      introText: 'Al seleccionar el botón de iniciar, se ejecutarán los siguientes cambios:',
      numbered: true,
      warningText: 'Al iniciar la liquidación, no hay forma de devolver los cambios'
    },
    TERMINATE_EMPLOYER_CONTRACTS: {
      title: 'LIQUIDAR CONTRATOS',
      lines: [
        'Todos los contratos que estén vinculados a esta empresa serán inactivados.',
        'Todas las personas y vehículos vinculados a contratos de esta empresa serán inactivados.',
        'Todos los usuarios vinculados a esta empresa serán inactivados.',
        'Todos los usuarios vinculados a contratos de esta empresa serán inactivados.'
      ],
      cancelText: 'Cerrar',
      confirmText: 'Iniciar',
      headerBar: true,
      sectionLabel: 'Instrucciones',
      introText: 'Al seleccionar el botón de iniciar, se ejecutarán los siguientes cambios:',
      numbered: true,
      warningText: 'Al iniciar la liquidación, no hay forma de devolver los cambios'
    },
    UNVERIFIED_EMAIL: {
      title: 'Correo pendiente de<br>verificación',
      lines: [
        'El correo ingresado ya está registrado, pero aún no ha sido verificado. Si deseas continuar, te enviaremos un correo con el enlace para completar la verificación.'
      ],
      secondaryText: 'Revisa también la carpeta de correo no deseado.',
      cancelText: 'Cerrar',
      confirmText: 'Enviar correo de verificación',
      customIcon: 'unverified-email',
      primaryBlue: true,
      centerText: true
    },
    VERIFIED_EMAIL: {
      title: 'Correo registrado y<br>verificado',
      lines: [
        'El correo ingresado ya está asociado a una cuenta y se encuentra verificado. Puedes iniciar sesión con este correo o cambiar tu contraseña si no la recuerdas.'
      ],
      cancelText: 'Restablecer la contraseña',
      confirmText: 'Iniciar sesión',
      customIcon: 'verified-email',
      primaryBlue: true,
      centerText: true,
      showCloseIcon: true
    }
  };

  constructor(
    private dialogRef: MatDialogRef<DialogAnuncioInformativosGeneralesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogAnuncioData
  ) {}

  ngOnInit(): void {
    const base = this.defaults[this.data.mode];
    this.vm = {
      title:       this.data.title       ?? base.title,
      lines:       this.data.lines       ?? base.lines,
      cancelText:  this.data.cancelText  ?? base.cancelText,
      confirmText: this.data.confirmText ?? base.confirmText,
      singleButton: base.singleButton,
      icon: base.icon,
      headerBar: base.headerBar,
      sectionLabel: base.sectionLabel,
      introText: base.introText,
      numbered: base.numbered,
      warningText: base.warningText,
      customIcon: base.customIcon,
      primaryBlue: base.primaryBlue,
      centerText: base.centerText,
      secondaryText: base.secondaryText,
      showCloseIcon: base.showCloseIcon
    };
  }

  onCancel(): void {
    this.dialogRef.close({ confirmed: false, mode: this.data.mode });
  }

  onConfirm(): void {
    if (this.data.mode === 'COMMENT') {
      this.commentTouched = true;
      if (!this.newComment.trim()) { return; }
      const previousComment = this.data.item?.previousComment?.trim() || '';
      const concatenated = previousComment
        ? `${previousComment}\n${this.newComment.trim()}`
        : this.newComment.trim();
      this.dialogRef.close({ confirmed: true, mode: this.data.mode, comment: concatenated });
      return;
    }
    this.dialogRef.close({ confirmed: true, mode: this.data.mode });
  }

  onCloseIconClick(): void {
    this.dialogRef.close({ confirmed: 'close', mode: this.data.mode });
  }
}
